import React, { useRef, useEffect, useState, useLayoutEffect, useMemo } from 'react';
import ImageIcon from '../../../assets/svg/image.svg?react';
import FJLocalStore from '../../../store/FJLocalStore';
import styles from './FJCreationPanel.module.less';
import PropTypes from 'prop-types';
import FJBlankPanel from '../FJBlankPanel/FJBlankPanel';
import FJTextToImagePresetItemContainer from '../../FJTextToImage/FJTextToImageRight/container/FJTextToImagePresetItemContainer';
import FJAppConstant from '../../../constants/FJAppConstant';
import FJBlankInVideoPanel from '../FJBlankPanel/FJBlankInVideoPanel';
import classNames from 'classnames';
import FJVideoContent from '../../../common/components/FJVideoPlayer/FJVideoContent.jsx';
import videoBgBase64 from '../../../assets/base64/video-bg.js';
import FJSourcePreviewItem from '../FJSourcePreviewItem/FJSourcePreviewItem.jsx';
import FJUtil from '../../../util/FJUtil.js';
import FJCreationPanelFooterContainer from '../FJCreationPanelFooter/container/FJCreationPanelFooterContainer.jsx';

const IMAGE_SPACE = 18;
const FJCreationPanel = props => {
    const {
        appType,
        creationList,
        previewImage,
        onDownload,
        onAddToMedia,
        reGenerate,
        active,
        isShowPresetImageBox = false,
        hideCompare = false,
        hideDownload = false,
        isShowBlankInVideoPanel = false,
        isShowAddToMediaButton = false,
        bkColor,
        isShowFooter,
        onPrivacySettingChange,
    } = props;
    const containerRef = useRef(null);
    const imgListRef = useRef([]);
    const boxRef = useRef(null);

    const [viewSize, setViewSize] = useState({ width: 0, height: 0 });
    const isVideoShowPreset = isShowPresetImageBox && FJAppConstant.AI_VIDEO_GENERATOR === appType;

    const isImageEditor = [
        FJAppConstant.TEXT_TO_IMAGE,
        FJAppConstant.IMAGE_TO_IMAGE,
        FJAppConstant.AI_PHOTO_EDITOR,
        FJAppConstant.AI_VIDEO_GENERATOR,
    ].includes(appType);

    const _updateViewSize = useMemo(
        () =>
            FJUtil.debounce(() => {
                if (boxRef.current) {
                    const viewRect = boxRef.current.getBoundingClientRect();

                    setViewSize({
                        width: viewRect.width - 19 * 2,
                        height: viewRect.height - 100 - 66,
                    });
                }
            }, 300),
        [],
    );

    // 初始化视图尺寸
    useLayoutEffect(() => {
        _updateViewSize();
        window.addEventListener('resize', _updateViewSize);
        return () => {
            window.removeEventListener('resize', _updateViewSize);
        };
    }, [_updateViewSize]);

    // 监听active状态，如果active状态为true，则更新视图尺寸
    useEffect(() => {
        if (active && viewSize.width <= 0 && viewSize.height <= 0) {
            _updateViewSize();
        }
    }, [active, _updateViewSize, viewSize.width, viewSize.height]);

    useEffect(() => {
        if (!active) return;
        const _updateUI = () => {
            if (boxRef.current && creationList?.length) {
                const viewRect = boxRef.current.getBoundingClientRect();
                let bottomHeight = 0;
                if (FJAppConstant.AI_PHOTO_EDITOR === appType) {
                    bottomHeight = 80;
                }

                let distance = 32;

                if (isImageEditor && isShowFooter) {
                    distance = distance + 90;
                }

                let viewWidth = viewRect.width - IMAGE_SPACE;
                let viewHeight = viewRect.height - distance - IMAGE_SPACE;
                if (isShowPresetImageBox && FJAppConstant.AI_VIDEO_GENERATOR === appType) {
                    viewHeight = viewHeight - 150;
                    viewWidth = viewWidth - 80;
                }

                const { width: originWidth, height: originHeight } = creationList[0];
                const ratio = originWidth / originHeight;
                let imgW = 0;
                let imgH = 0;
                let padding = 0;
                if (creationList.length < 3) {
                    imgW = (viewWidth - IMAGE_SPACE * (creationList.length - 1)) / creationList.length;

                    imgH = imgW / ratio;

                    if (imgH > viewHeight - bottomHeight) {
                        imgH = viewHeight - bottomHeight;
                        imgW = imgH * ratio;
                    }
                } else {
                    let rowNum = 2;
                    let columnNum = Math.ceil(creationList.length / rowNum);
                    if (ratio >= 1) {
                        imgW = (viewWidth - IMAGE_SPACE * (columnNum - 1)) / columnNum;
                        imgH = imgW / ratio;
                        if (imgH * rowNum + IMAGE_SPACE * (rowNum - 1) > viewHeight) {
                            imgH = (viewHeight - IMAGE_SPACE * (rowNum - 1)) / rowNum;
                            imgW = imgH * ratio;
                        }
                    } else {
                        imgH = (viewHeight - IMAGE_SPACE * (rowNum - 1)) / rowNum;
                        imgW = imgH * ratio;
                        if (imgW * columnNum + IMAGE_SPACE * (columnNum - 1) > viewWidth) {
                            imgW = (viewWidth - IMAGE_SPACE * (columnNum - 1)) / columnNum;
                            imgH = imgW / ratio;
                        }
                    }
                    if (imgW * creationList.length + IMAGE_SPACE * (creationList.length - 1) <= viewWidth) {
                        rowNum = 1;
                        columnNum = Math.ceil(creationList.length / rowNum);
                        imgW = (viewWidth - IMAGE_SPACE * (columnNum - 1)) / columnNum;
                        imgH = imgW / ratio;
                        if (imgH > viewHeight) {
                            imgH = viewHeight;
                            imgW = imgH * ratio;
                        }
                    }
                    if (rowNum == 2) {
                        const containerWidth = viewWidth + IMAGE_SPACE;
                        const count = Math.ceil(creationList.length / rowNum);
                        padding = Math.floor((containerWidth - (imgW + IMAGE_SPACE) * count) / 2);
                    }
                }
                imgListRef.current.forEach(item => {
                    if (item) {
                        item.style.width = imgW + 'px';
                        item.style.height = imgH + 'px';
                    }
                });
                if (!isVideoShowPreset) {
                    containerRef.current.style.width = viewWidth + IMAGE_SPACE + 'px';
                    containerRef.current.style.height = viewHeight + IMAGE_SPACE + 'px';
                }
                containerRef.current.style.padding = ` 0 ${padding}px`;
            }
        };

        // TODO 在编辑页中会有一个缩放的动画导致获取尺寸不正确，所以加个延时
        if (window.isEditPage) {
            setTimeout(() => {
                _updateUI();
            }, 300);
        } else {
            _updateUI();
        }

        const _debounceUpdateUI = () => {
            FJUtil.debounce(_updateUI, 300)();
        };

        window.addEventListener('resize', _debounceUpdateUI);
        return () => {
            window.removeEventListener('resize', _debounceUpdateUI);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creationList, active, isShowPresetImageBox, isVideoShowPreset, appType, isImageEditor, isShowFooter]);

    const renderItem = () => {
        if (isShowPresetImageBox) {
            return creationList.map((item, idx) => {
                return (
                    <div className={styles.img_box} key={idx} ref={ref => (imgListRef.current[idx] = ref)}>
                        <FJTextToImagePresetItemContainer presetData={item} />
                    </div>
                );
            });
        }
        if (FJAppConstant.AI_VIDEO_GENERATOR === appType) {
            const item = creationList[0];
            return (
                <div className={styles.video_box}>
                    <FJVideoContent
                        url={item.videoUrl}
                        isAutoPlay
                        viewSize={viewSize}
                        isLoop={true}
                        scope='default'
                        ratio={item.ratio}
                        isKeySpace={false}
                        style={{ borderRadius: '12px' }}
                    />
                </div>
            );
        }
        if (FJAppConstant.AI_PHOTO_EDITOR === appType) {
            return creationList.slice(0, 1).map((item, idx) => (
                <React.Fragment key={item.type + item.id}>
                    <div className={styles.ai_photo_editor_box} ref={ref => (imgListRef.current[idx] = ref)}>
                        <FJSourcePreviewItem
                            appType={appType}
                            item={item}
                            onPrivacySettingChange={onPrivacySettingChange}
                            onDownload={onDownload}
                            onAddToMedia={onAddToMedia}
                            reGenerate={reGenerate}
                            onClick={previewImage}
                            isShowSelected={false}
                            hideSelect={true}
                            hideDelete={true}
                            hideCompare={item.inputImages?.length > 0} // 如果存在多张图，则不显示对比按钮
                            hideDownload={hideDownload}
                            isShowAddToMediaButton={isShowAddToMediaButton}
                            isShowGenerating={true}
                            creationListLength={creationList.length}
                            isCreation={true}
                        />
                    </div>
                </React.Fragment>
            ));
        }

        return creationList.map((item, idx) => {
            return (
                <div className={styles.img_box} key={item.type + item.id} ref={ref => (imgListRef.current[idx] = ref)}>
                    <FJSourcePreviewItem
                        appType={appType}
                        item={item}
                        onPrivacySettingChange={onPrivacySettingChange}
                        onDownload={onDownload}
                        onAddToMedia={onAddToMedia}
                        reGenerate={reGenerate}
                        onClick={previewImage}
                        isShowSelected={false}
                        hideSelect={true}
                        hideDelete={true}
                        hideCompare={hideCompare}
                        hideDownload={hideDownload}
                        isShowAddToMediaButton={isShowAddToMediaButton}
                        isShowGenerating={true}
                        creationListLength={creationList.length}
                        isCreation={true}
                    />
                </div>
            );
        });
    };

    return (
        <div
            className={classNames(styles.content, {
                [styles.not_padding]: isShowBlankInVideoPanel && !creationList?.length,
                [styles.video_padding]: isVideoShowPreset,
                [styles.img_no_padding]: creationList?.length && isImageEditor,
            })}
            style={{ display: active ? (isVideoShowPreset ? 'flex' : 'block') : 'none' }}
            ref={boxRef}
        >
            {isShowPresetImageBox && FJAppConstant.AI_VIDEO_GENERATOR === appType && (
                <>
                    <div className={styles.cover_box}>
                        <img src={videoBgBase64} alt='' />
                    </div>
                    <div className={styles.title_main}>
                        {FJLocalStore._('turn-your-creative-ideas-into-reality-with-AI')}
                    </div>
                    <div className={styles.title_sub}>
                        {FJLocalStore._('easily-create-AI-videos-with-text-or-image-prompts')}
                    </div>
                </>
            )}
            {creationList?.length ? (
                <div className={styles.inner} ref={containerRef}>
                    {renderItem()}
                    <FJCreationPanelFooterContainer appType={appType} />
                </div>
            ) : isShowBlankInVideoPanel ? (
                <FJBlankInVideoPanel active={active} bkColor={bkColor} />
            ) : (
                <FJBlankPanel
                    icon={<ImageIcon />}
                    text={
                        appType === FJAppConstant.IMAGE_EXTENDER
                            ? FJLocalStore._('no-results-yet')
                            : FJLocalStore._('no-creations-yet')
                    }
                />
            )}
        </div>
    );
};
FJCreationPanel.propTypes = {
    appType: PropTypes.string,
    creationList: PropTypes.array,
    previewImage: PropTypes.func,
    onAddToMedia: PropTypes.func,
    reGenerate: PropTypes.func,
    onDownload: PropTypes.func,
    active: PropTypes.bool,
    isShowPresetImageBox: PropTypes.bool,
    hideCompare: PropTypes.bool,
    hideDownload: PropTypes.bool,
    isShowBlankInVideoPanel: PropTypes.bool,
    isShowAddToMediaButton: PropTypes.bool,
    bkColor: PropTypes.string,
    renderBottom: PropTypes.func,
    onPrivacySettingChange: PropTypes.func,
};
export default FJCreationPanel;
